import { Injectable } from "@angular/core";
import { CustomRule, Filter, DisplayOptions, AdvancedConditions, CosmeticOptions } from "../../models/filter-models";
import { DisplayType, LabelSize } from "../../models/enums";
import { Tier, TierList } from "../../models/item-data-models";
import { ItemDataService } from "../item-data.service";
import { filterHeader } from "../../filter-template";
import { RuleGeneratorService } from "./rule-generator.service";
import { CurrencyTierService } from "../tiering/currency-tier.service";
import { poe1 } from "../../../game";
import { getStackPromotions, ORDERED_CURRENCY_TIERS, sortStackPromoGroups } from "./currency-stack-promotion";

interface StackPromoGroup {
  targetTier: Tier;
  stackSize: number;
  baseTypes: string[];
}

@Injectable({
  providedIn: 'root'
})
export class CurrencyGeneratorService {
  private tieredItemTypes: string[];
  private itemColors!: { normal: string, magic: string, rare: string, currency: string };
  private hiddenCurrencyColor!: string;
  private hiddenCurrencyBackgroundColor!: string;

  currencyWithStacks: string[];
  private currencyWithStacksSet: Set<string>;
  currencyTypesToPromoteSmallStacks: string[] = poe1 ? ['Scarabs', 'Delirium Orbs'] : [];
  private currencyTypesToPromoteSmallStacksSet = new Set(this.currencyTypesToPromoteSmallStacks);

  static cache: { [key: string]: string } = {};

  constructor(private itemDataService: ItemDataService, private ruleGenerator: RuleGeneratorService, private currencyTierService: CurrencyTierService) {
    this.tieredItemTypes = this.itemDataService.getTieredCurrencyItemTypes();
    this.currencyWithStacks = this.itemDataService.getStackableCurrency();
    this.currencyWithStacksSet = new Set(this.currencyWithStacks);
    this.itemColors = this.itemDataService.getItemColors();
    this.hiddenCurrencyColor = this.itemColors.currency.replace('rgb(', 'rgba(').replace(')', ',0.6)');
    this.hiddenCurrencyBackgroundColor = 'rgba(0, 0, 0, 0.6)';
  }

  public generateTieredCurrencyRules(filter: Filter, disableCache: boolean = false) {
    const cacheKey =
      String(filter.stackedGeneralCurrencyPromotionEnabled)
      + String(filter.stackedCurrencyMaxPromoteSteps)
      + JSON.stringify(filter.stackedGeneralCurrencyNeverPromote)
      + String(filter.promoteForcedHiddenCurrency)
      + String(filter.promoteForcedHiddenCurrencyOneTierStackSize)
      + String(filter.promoteForcedHiddenCurrencyTwoTiersStackSize)
      + JSON.stringify(filter.tierCosmetics)
      + JSON.stringify(filter.divCardTierCosmetics)
      + JSON.stringify(filter.divCardCosmeticsSameAsTierCosmetics)
      + (filter.ruthless ? 'ruthless' : '')
      + CurrencyTierService.cacheBreaker
      + (disableCache ? new Date().getTime() : '');

    if (CurrencyGeneratorService.cache[cacheKey])
      return CurrencyGeneratorService.cache[cacheKey];

    const tiers: Tier[] = Object.values(Tier);
    const tierListData = this.currencyTierService.getTierList();
    const tierDataByTiers = new Map(tierListData.map(t => [t.tier, t]));
    const absoluteCutoffs = this.currencyTierService.getAbsoluteTierThresholds();
    const tierCosmeticsByTiers = new Map(filter.tierCosmetics.map(t => [t.tier, t]));
    const divCardTierCosmeticsByTiers = new Map(filter.divCardTierCosmetics.map(t => [t.tier, t]));
    const ruthlessDisabledTypes = filter.ruthless
      ? new Set(this.itemDataService.getRuthlessDisabledCurrencyTypes())
      : null;

    const maxSteps = filter.stackedCurrencyMaxPromoteSteps ?? 2;
    const forcedHiddenByName = this.buildForcedHiddenByName(filter, tierListData);

    let text = '';

    for (let itemType of this.tieredItemTypes) {
      if (itemType === 'Uniques') continue;
      if (ruthlessDisabledTypes?.has(itemType)) continue;

      const isDivCard = itemType === 'Divination Cards';
      const cosmeticsByTier = isDivCard ? divCardTierCosmeticsByTiers : tierCosmeticsByTiers;
      const displayType = isDivCard ? DisplayType.DivCardTier : DisplayType.Tier;

      text += filterHeader.replace('{header}', itemType);
      for (let tier of tiers) {
        const tierData = tierDataByTiers.get(tier);
        if (tierData) {
          const tieredBaseTypes = tierData.itemTypes.find(i => i.itemType === itemType)?.baseTypes || [];
          const valueByName = new Map(tieredBaseTypes.map(bt => [bt.name, bt.value]));

          const baseTypePerClass: { [key: string]: string[] } = {};
          for (const bt of tieredBaseTypes) {
            const flat = this.itemDataService.getFlatItemDataByBaseTypeName(bt.name);
            if (!flat) continue;
            const list = baseTypePerClass[flat.itemClass];
            if (list) list.push(bt.name);
            else baseTypePerClass[flat.itemClass] = [bt.name];
          }

          const hiddenTier = cosmeticsByTier.get(tier)?.displayOptions?.hidden;

          for (let itemClass in baseTypePerClass) {
            let baseTypesOfClass = baseTypePerClass[itemClass];
            const displayItemClass = tier + '-Tier ' + (itemClass === 'Stackable Currency' ? itemType : itemClass);

            if (filter.stackedGeneralCurrencyPromotionEnabled) {
              const isTypeToPromoteSmallStacks = this.currencyTypesToPromoteSmallStacksSet.has(itemType);
              if (baseTypesOfClass.some(b => this.currencyWithStacksSet.has(b)) || isTypeToPromoteSmallStacks) {
                let stackableBaseTypes = isTypeToPromoteSmallStacks
                  ? baseTypesOfClass
                  : baseTypesOfClass.filter((b: string) => this.currencyWithStacksSet.has(b));

                // Scarabs / Delirium Orbs: only promote from B–F (legacy behaviour)
                if (isTypeToPromoteSmallStacks && ![Tier.BOrange, Tier.CYellow, Tier.DWhite, Tier.EVisible, Tier.FHidden].includes(tier)) {
                  stackableBaseTypes = [];
                }

                const neverPromote = filter.stackedGeneralCurrencyNeverPromote ?? [];
                if (neverPromote.length > 0 && !neverPromote.includes('None')) {
                  stackableBaseTypes = stackableBaseTypes.filter(b => !neverPromote.includes(b));
                }

                if (stackableBaseTypes.length > 0) {
                  const groups = this.buildStackPromoGroups(
                    stackableBaseTypes,
                    valueByName,
                    forcedHiddenByName,
                    tier,
                    absoluteCutoffs,
                    isTypeToPromoteSmallStacks ? 1 : maxSteps,
                    filter,
                  );

                  for (const group of groups) {
                    const rule = new CustomRule();
                    rule.itemClass = itemClass;
                    rule.baseTypes = group.baseTypes;
                    rule.advancedConditions = new AdvancedConditions({ minStackSize: group.stackSize });

                    rule.displayOptions = new DisplayOptions({ displayType: displayType, displayTypeTier: group.targetTier });

                    const label = 'Stacks of ' + group.stackSize + '+ → ' + group.targetTier + '-Tier ' + (itemClass === 'Stackable Currency' ? itemType : itemClass);
                    text += '\n' + this.ruleGenerator.generateRule(rule, filter, 'currency', label) + '\n';
                  }
                }
              }
            }

            const rule = new CustomRule();
            rule.itemClass = itemClass;
            rule.baseTypes = baseTypesOfClass;
            if (itemClass === 'Vault Keys' && hiddenTier) {
              rule.displayOptions = new DisplayOptions({
                displayType: DisplayType.Custom,
                hidden: false,
                cosmeticOptions: new CosmeticOptions({
                  fontSize: LabelSize.Smallest,
                  backgroundColor: this.hiddenCurrencyBackgroundColor,
                  textColor: this.hiddenCurrencyColor,
                  borderColor: this.hiddenCurrencyColor,
                })
              });
            } else {
              rule.displayOptions = new DisplayOptions({ displayType: displayType, displayTypeTier: tier });
            }

            text += '\n' + this.ruleGenerator.generateRule(rule, filter, 'currency', displayItemClass) + '\n';
          }
        }
      }
    }

    CurrencyGeneratorService.cache[cacheKey] = text;

    return text;
  }

  private buildForcedHiddenByName(filter: Filter, tierListData: TierList): Set<string> {
    const forced = new Set<string>();

    for (const tierData of tierListData) {
      for (const itemType of tierData.itemTypes) {
        for (const bt of itemType.baseTypes) {
          if (bt.hiddenByQuickFilter) forced.add(bt.name);
        }
      }
    }

    for (const custom of filter.customCurrencyTiers ?? []) {
      if (custom.tier === Tier.FHidden && !custom.shownExplicitly) {
        forced.add(custom.name);
      }
    }

    return forced;
  }

  private buildStackPromoGroups(
    stackableBaseTypes: string[],
    valueByName: Map<string, number | undefined>,
    forcedHiddenByName: Set<string>,
    sourceTier: Tier,
    absoluteCutoffs: Map<Tier, number>,
    maxSteps: number,
    filter: Filter,
  ): StackPromoGroup[] {
    const groups = new Map<string, string[]>();
    const oneTier = filter.promoteForcedHiddenCurrencyOneTierStackSize ?? (poe1 ? 10 : 5);
    const twoTiers = filter.promoteForcedHiddenCurrencyTwoTiersStackSize ?? (poe1 ? 20 : 10);
    const forcedBuckets = [oneTier, twoTiers].sort((a, b) => a - b);

    for (const name of stackableBaseTypes) {
      const isForcedHidden = sourceTier === Tier.FHidden && forcedHiddenByName.has(name);
      // Forced-hidden stays hidden unless Promote Forced-Hidden is on (fixed sizes only)
      if (isForcedHidden && !filter.promoteForcedHiddenCurrency) continue;

      const useFixed = isForcedHidden;
      const promotions = getStackPromotions({
        sourceTier,
        value: useFixed ? undefined : valueByName.get(name),
        absoluteCutoffs,
        maxSteps,
        buckets: useFixed ? forcedBuckets : undefined,
      });
      for (const promo of promotions) {
        const key = promo.targetTier + '|' + promo.stackSize;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(name);
      }
    }

    const result: StackPromoGroup[] = [];
    for (const [key, baseTypes] of groups) {
      const [targetTier, stackStr] = key.split('|');
      result.push({ targetTier: targetTier as Tier, stackSize: Number(stackStr), baseTypes });
    }

    return sortStackPromoGroups(result);
  }

  getHigherTier(tier: Tier) {
    const index = ORDERED_CURRENCY_TIERS.indexOf(tier);
    return ORDERED_CURRENCY_TIERS[Math.max(0, index - 1)];
  }
}
